"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { motion } from "@/lib/motion";
import { PrismicRichText } from "@prismicio/react";
import type { RichTextField } from "@prismicio/client";
import { scrollToId } from "@/lib/utils";
import { HeroGlow } from "@/components/ui";

type RichTextSpan = {
  type: string;
  start: number;
  end: number;
  data?: { url?: string; target?: string; link_type?: string };
};
type RichTextNode = { type: string; text: string; spans: RichTextSpan[] };

export type PrismicLegalDoc = {
  uid: string;
  data: {
    title?: string;
    category?: string;
    last_updated?: string | null;
    description?: string;
    intro?: RichTextNode[];
    content_blocks?: Array<{
      heading?: string | null;
      body?: RichTextNode[];
      table_html?: string | null;
    }>;
    meta_title?: string;
    meta_description?: string;
  };
};

type Props = {
  doc: PrismicLegalDoc;
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export default function PrismicLegalPost({ doc }: Props) {
  const blocks = doc.data.content_blocks ?? [];
  const sectionIds = blocks.map((b, i) =>
    b.heading
      ? b.heading
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "")
      : `section-${i}`
  );

  const [activeId, setActiveId] = useState<string>(sectionIds[0] ?? "");

  useEffect(() => {
    const OFFSET = 120;
    const update = () => {
      let current = sectionIds[0] ?? "";
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= OFFSET) current = id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, [sectionIds.join(",")]);

  const lastUpdated = formatDate(doc.data.last_updated as string | null);

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="relative isolate min-h-screen overflow-y-clip"
      style={{ background: "var(--background)" }}
    >
      {/* These pages have no hero section — HeroGlow bounds itself to a band at
          the top of the document, so it lands where it does everywhere else. */}
      <HeroGlow align="left" behind />

      <div className="relative mx-auto max-w-7xl px-4 pt-24 pb-12 sm:px-6 lg:px-8 lg:pt-32 lg:pb-16">
        <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-16 xl:gap-20">
          <aside className="hidden lg:block">
            <div className="sticky top-20 flex max-h-[calc(100vh-6rem)] flex-col">
              <div
                className="mb-6 shrink-0 pb-5"
                style={{ borderBottom: "1px solid var(--border)" }}
              >
                <Link
                  href="/legal"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
                  style={{ color: "var(--foreground)" }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLElement).style.color = "var(--muted-foreground)")
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLElement).style.color = "var(--foreground)")
                  }
                >
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="h-4 w-4 shrink-0"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 8H3m0 0l4-4M3 8l4 4"
                    />
                  </svg>
                  Legal
                </Link>
                {lastUpdated && (
                  <p className="mt-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
                    Updated {lastUpdated}
                  </p>
                )}
              </div>

              <nav aria-label="Table of contents" className="flex min-h-0 flex-1 flex-col">
                <p
                  className="mb-3 shrink-0 text-[11px] font-semibold tracking-widest uppercase"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  On this page
                </p>
                <ul className="toc-scroll min-h-0 flex-1 space-y-0.5 overflow-y-auto pr-1">
                  {blocks.map((b, i) => {
                    const id = sectionIds[i];
                    const isActive = activeId === id;
                    return (
                      <li key={id}>
                        <button
                          onClick={() => scrollToId(id)}
                          className="w-full rounded-md px-3 py-1.5 text-left text-sm leading-snug transition-all duration-150"
                          style={{
                            color: isActive ? "var(--foreground)" : "var(--muted-foreground)",
                            background: isActive ? "rgba(247,247,248,0.07)" : "transparent",
                            fontWeight: isActive ? 500 : 400,
                            borderLeft: isActive
                              ? "2px solid var(--foreground)"
                              : "2px solid transparent",
                          }}
                        >
                          {b.heading}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </aside>

          <motion.article
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="min-w-0"
          >
            <header className="mb-10 pb-8" style={{ borderBottom: "1px solid var(--border)" }}>
              <h1
                className="text-3xl font-bold tracking-tight sm:text-4xl"
                style={{ color: "var(--foreground)" }}
              >
                {doc.data.title as string}
              </h1>
              {doc.data.intro && Array.isArray(doc.data.intro) && doc.data.intro.length > 0 && (
                <div
                  className="3xl:text-lg mt-5 text-base leading-relaxed"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  <PrismicRichText field={doc.data.intro as unknown as RichTextField} />
                </div>
              )}
            </header>

            <details
              className="mb-8 overflow-hidden rounded-xl lg:hidden"
              style={{ background: "var(--surface-1)", border: "1px solid var(--border)" }}
            >
              <summary
                className="cursor-pointer px-5 py-3 text-sm font-medium select-none"
                style={{ color: "var(--foreground)" }}
              >
                On this page
              </summary>
              <ul className="space-y-0.5 px-3 pb-3">
                {blocks.map((b, i) => (
                  <li key={sectionIds[i]}>
                    <button
                      onClick={() => scrollToId(sectionIds[i])}
                      className="w-full rounded-md px-3 py-1.5 text-left text-sm transition-colors"
                      style={{ color: "var(--muted-foreground)" }}
                    >
                      {b.heading}
                    </button>
                  </li>
                ))}
              </ul>
            </details>

            <div className="space-y-12">
              {blocks.map((block, i) => (
                <section key={sectionIds[i]} id={sectionIds[i]} className="scroll-mt-28">
                  {block.heading && (
                    <h2
                      className="3xl:text-3xl mb-4 text-xl leading-snug font-semibold"
                      style={{ color: "var(--foreground)" }}
                    >
                      {block.heading}
                    </h2>
                  )}
                  <div className="legal-prose">
                    {block.table_html && (
                      <div
                        className="mb-6 overflow-x-auto"
                        dangerouslySetInnerHTML={{ __html: block.table_html }}
                      />
                    )}
                    <PrismicRichText
                      field={block.body as unknown as RichTextField}
                      components={{
                        paragraph: ({ children }) => (
                          <p
                            className="3xl:text-lg mb-4 text-[15px] leading-[1.8] last:mb-0"
                            style={{ color: "var(--muted-foreground)" }}
                          >
                            {children}
                          </p>
                        ),
                        listItem: ({ children }) => (
                          <li
                            className="3xl:text-lg flex gap-3 text-[15px] leading-[1.8]"
                            style={{ color: "var(--muted-foreground)" }}
                          >
                            <span
                              className="mt-[11px] h-1 w-1 flex-none shrink-0 rounded-full"
                              style={{ background: "var(--muted-foreground)", opacity: 0.5 }}
                            />
                            <span>{children}</span>
                          </li>
                        ),
                        list: ({ children }) => (
                          <ul className="mb-4 space-y-2 last:mb-0">{children}</ul>
                        ),
                        oList: ({ children }) => (
                          <ol
                            className="3xl:text-lg mb-4 list-inside list-decimal space-y-2 text-[15px] leading-[1.8] last:mb-0"
                            style={{ color: "var(--muted-foreground)" }}
                          >
                            {children}
                          </ol>
                        ),
                        oListItem: ({ children }) => (
                          <li
                            className="3xl:text-lg ml-4 text-[15px] leading-[1.8]"
                            style={{ color: "var(--muted-foreground)" }}
                          >
                            {children}
                          </li>
                        ),
                        heading3: ({ children }) => (
                          <h3
                            className="mt-6 mb-2 text-lg font-semibold first:mt-0"
                            style={{ color: "var(--foreground)" }}
                          >
                            {children}
                          </h3>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-semibold" style={{ color: "var(--foreground)" }}>
                            {children}
                          </strong>
                        ),
                        hyperlink: ({ children, node }) => {
                          const url = (node.data as { url?: string }).url ?? "#";
                          const isExternal = !url.includes("proxidize.com") && !url.startsWith("/");
                          return (
                            <a
                              href={url}
                              className="transition-colors"
                              style={{
                                color: "var(--foreground)",
                                textDecoration: "underline",
                                textUnderlineOffset: "3px",
                              }}
                              {...(isExternal
                                ? { target: "_blank", rel: "noopener noreferrer" }
                                : { rel: "noopener" })}
                            >
                              {children}
                            </a>
                          );
                        },
                      }}
                    />
                  </div>
                </section>
              ))}
            </div>
          </motion.article>
        </div>
      </div>
    </main>
  );
}
